<template>
  <div class="p-4">
    <!-- Header Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      <!-- Today's Assigned -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Colis Affectes') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayAssigned || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Today's Delivered -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Colis Livres') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayDelivered || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Today's Returns -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-orange-100 text-orange-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Colis Retournes') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayReturns || 0 }}</p>
          </div>
        </div>
      </div>

      <!-- Validated Returns -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center">
          <div class="p-3 rounded-full bg-purple-100 text-purple-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-gray-500 text-sm">{{ $t('Retours Confirmes') }}</p>
            <p class="text-2xl font-semibold text-gray-800">{{ stats.todayValidatedReturns || 0 }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Second Row Stats -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <!-- Pending Returns -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-red-100 text-red-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">{{ $t('Retours a Valider') }}</p>
              <p class="text-2xl font-semibold text-gray-800">{{ stats.pendingReturns || 0 }}</p>
            </div>
          </div>
          <router-link
            to="/stockist-dashboard/returns"
            class="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
          >
            {{ $t('Valider') }}
          </router-link>
        </div>
      </div>

      <!-- Ready to Ship -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-yellow-100 text-yellow-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">{{ $t('Prets a Affecter') }}</p>
              <p class="text-2xl font-semibold text-gray-800">{{ stats.readyToShip || 0 }}</p>
            </div>
          </div>
          <router-link
            to="/stockist-dashboard/assign"
            class="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
          >
            {{ $t('Affecter') }}
          </router-link>
        </div>
      </div>

      <!-- Today's Couriers -->
      <div class="bg-white rounded-lg shadow p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="p-3 rounded-full bg-indigo-100 text-indigo-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <div class="ml-4">
              <p class="text-gray-500 text-sm">{{ $t('Livreurs Actifs') }}</p>
              <p class="text-2xl font-semibold text-gray-800">{{ stats.todayCouriersCount || 0 }}</p>
            </div>
          </div>
          <router-link
            to="/stockist-dashboard/couriers"
            class="px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
          >
            {{ $t('Voir') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Status Breakdown -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <h3 class="text-lg font-semibold mb-4">{{ $t('Repartition par Statut') }}</h3>
      <div class="flex flex-wrap gap-3">
        <div
          v-for="(count, status) in stats.statusCounts"
          :key="status"
          :class="[
            'px-4 py-2 rounded-full text-sm font-medium',
            getStatusClass(status)
          ]"
        >
          {{ status }}: {{ count }}
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="bg-white rounded-lg shadow p-4">
      <h3 class="text-lg font-semibold mb-4">{{ $t('Actions Rapides') }}</h3>
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <router-link
          to="/stockist-dashboard/assign"
          class="flex flex-col items-center p-4 border rounded-lg hover:bg-blue-50 transition-colors"
        >
          <svg class="w-8 h-8 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
          </svg>
          <span class="text-sm font-medium text-gray-700">{{ $t('Affecter des colis') }}</span>
        </router-link>

        <router-link
          to="/stockist-dashboard/returns"
          class="flex flex-col items-center p-4 border rounded-lg hover:bg-orange-50 transition-colors"
        >
          <svg class="w-8 h-8 text-orange-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
          </svg>
          <span class="text-sm font-medium text-gray-700">{{ $t('Confirmer des retours') }}</span>
        </router-link>

        <router-link
          to="/stockist-dashboard/stats"
          class="flex flex-col items-center p-4 border rounded-lg hover:bg-green-50 transition-colors"
        >
          <svg class="w-8 h-8 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>
          <span class="text-sm font-medium text-gray-700">{{ $t('Mes statistiques') }}</span>
        </router-link>

        <router-link
          to="/stockist-dashboard/couriers"
          class="flex flex-col items-center p-4 border rounded-lg hover:bg-purple-50 transition-colors"
        >
          <svg class="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
          <span class="text-sm font-medium text-gray-700">{{ $t('Mes livreurs') }}</span>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StockistDashboard',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      stats: {}
    };
  },
  async mounted() {
    if (this.currentUser && this.currentUser._id) {
      await this.loadStats();
    }
  },
  watch: {
    // Reload when warehouse changes in header
    idWharhouse: {
      handler: function() {
        this.loadStats();
      },
      deep: true
    }
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const params = {};
        // Use selected country from header
        if (this.idWharhouse && this.idWharhouse.country) {
          params.country = this.idWharhouse.country;
        }

        const res = await this.$server.getDashboardStats('stockistDashboard', params);
        if (res && res.content) {
          this.stats = res.content;
        } else if (res && !res.content) {
          this.stats = res;
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
      this.loading = false;
    },

    getStatusClass(status) {
      const classes = {
        'delivered': 'bg-green-100 text-green-800',
        'shipped': 'bg-blue-100 text-blue-800',
        'prepared': 'bg-indigo-100 text-indigo-800',
        'to prepare': 'bg-gray-100 text-gray-800',
        'cancelled': 'bg-red-100 text-red-800',
        'refused': 'bg-red-100 text-red-800',
        'unreachable': 'bg-yellow-100 text-yellow-800',
        'remind': 'bg-purple-100 text-purple-800',
        'return': 'bg-orange-100 text-orange-800',
        'return validated': 'bg-green-100 text-green-800',
        'pending return': 'bg-orange-100 text-orange-800',
        'paid': 'bg-green-100 text-green-800',
        'processed': 'bg-green-100 text-green-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    }
  }
};
</script>

<style scoped>
</style>
